import { SendungsstatusModel } from '../../src/models/SendungsstatusModel';
import { StateModel_Auftragserfassung } from '../redux/store';
import { AdresseModel } from '../models/AdresseModel';
import {
  EMPFAENGER,
  VERSENDER,
} from '../layouts/tms/auftragserfassung/adresse/AdresseTypen';

/**
 * Konvertiert ein Iso-Datum in das deutsche Format mit Beachtung der UTC Zeit.
 * @param isodate 2021-10-15T00:00:00Z
 * @param locale Datumslokalisierung, default "de-DE" ~ Deutsch
 * @returns date "15.10.2021"-Format (locale abhängig)
 */

export const utcIsoDateToLocaleDate = (
  isodate: string | number | Date,
  locale = 'de-DE'
): string => {
  let date: Date | string = '';
  if (isodate) {
    date = new Date(isodate);
    date = date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  return date;
};

/**
 * Die Methode utcIsoDateToLocaleDate liefert bei
 * einem Date '08.11.2025 23:25:00' den '19.11.2025', also +1 Tag.
 * Deshalb wird hier die Uhrzeit abgeschnitten, damit keine Verschiebung erfolgt
 * @param isodate 2025-11-08T23:25:00Z
 * @param locale Datumslokalisierung, default "de-DE" ~ Deutsch
 */
export const isoDateToLocalDateIgnoreTime = (
  isodate: string | number | Date,
  locale = 'de-DE'
): string => {
  if (!isodate) return '';

  // ISO-Date als String
  const iso =
    typeof isodate === 'string' ? isodate : new Date(isodate).toISOString();

  // Nur Datumsteil extrahieren (YYYY-MM-DD)
  const datePart = iso.substring(0, 10);

  // Sicher in lokales Datum parsen, OHNE Uhrzeitverschiebung
  const [y, m, d] = datePart.split('-');

  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(
    locale,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
  );
};

/**
 * Prüft ob datum Gültig ist und > 1970-01-01
 * @param isodate  Format YYYY-MM-DD
 * @returns boolean
 */
export const isoDateIsValid = (isodate: string): boolean => {
  if (isodate.length != 10) return false;
  const splitted = isodate.split('-');
  const year = splitted[0];
  const month = splitted[1];
  const day = splitted[2];
  return parseInt(year) >= 1970 && parseInt(month) > 0 && parseInt(day) > 0;
};

/**
 * Konvertiert ein Iso-Datum in das deutsche Format mit Beachtung der UTC Zeit.
 * @param isodate 2021-10-15T00:00:00Z
 * @param locale Datumslokalisierung, default "de-DE" ~ Deutsch
 * @returns date "15.10.2021, 12:51"-Format (locale abhängig)
 */
export const utcIsoDateToLocaleDateAndTime = (
  isodate: string | number | Date,
  locale = 'de-DE'
): string => {
  let date: Date | string = '';
  if (isodate) {
    date = new Date(isodate);

    date = date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date;
};
/**
 * Erzeugt das genaue Datums/Zeit Objekt anhand des TimezoneOffset
 * (TimezoneOffset Bsp.: Sommerzeit in DE = -120)
 * @param isodate
 */
export const convertToLocaleDateAndTime = (
  isodate: string | number | Date
): Date => {
  let date: Date | string = '';
  let off = 0;
  if (isodate) {
    date = new Date(isodate);
  } else date = new Date(Date.now());
  if (date.getTimezoneOffset() != 0) {
    off = (date.getTimezoneOffset() / 60) * -1;
    date.setHours(date.getHours() + off);
  }
  return <Date>date;
};

/**
 * Konvertiert ein Iso-Datum in das deutsche Format OHNE Beachtung der UTC Zeit.
 * @param isodate 2021-10-15T00:00:00Z
 * @param locale Datumslokalisierung, default "de-DE" ~ Deutsch
 * @returns date "15.10.2021, 12:51"-Format (locale abhängig)
 */
export const utcIsoDateToLocaleDateAndTimeUTC = (
  isodate: string | number | Date,
  locale = 'de-DE'
): string => {
  let date: Date | string = '';
  if (isodate) {
    date = new Date(isodate);

    date = date.toLocaleDateString(locale, {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date;
};

/**
 * Konvertiert ein Iso-Datum in das deutsche Format OHNE Beachtung der UTC Zeit.
 * @param isodate 2021-10-15T00:00:00Z
 * @returns date "15.10.2021 12:51"-Format
 */
export const utcIsoDateToString = (isodate: string): string => {
  if (isodate.indexOf('T') < 0) return isodate;
  const sub = isodate;
  const year = sub.substr(0, 4);
  const month = sub.substr(5, 2);
  const day = sub.substr(8, 2);
  const hour = sub.substr(11, 2);
  const minute = sub.substr(14, 2);

  const ret = day + '.' + month + '.' + year + ' ' + hour + ':' + minute;
  return ret;
};

/**
 * Konvertiert ein Iso-Datum in dasAnzeigeformat.
 * @param datestring 01.02.2024 12:34
 * @returns date "202402-01-T12:34"-Format
 */
export const stringToUtcIsoDate = (datestring: string): string => {
  if (datestring.indexOf('T') > 0) return datestring;
  const sub = datestring;
  const year = sub.substr(6, 4);
  const month = sub.substr(3, 2);
  const day = sub.substr(0, 2);
  const hour = sub.substr(11, 2);
  const minute = sub.substr(14, 2);

  const ret = year + '-' + month + '-' + day + 'T' + hour + ':' + minute;
  return ret;
};
/**
 * Konvertiert ein Iso-Datum in das deutsche Format mit Beachtung der UTC Zeit.
 * @param isodate 2021-10-15T00:00:00Z
 * @param locale Datumslokalisierung, default "de-DE" ~ Deutsch
 * @returns date "Mo 15.10.2021, 12:51"-Format (locale abhängig)
 */
export const utcIsoDateToLocaleDateAndTimeAndDay = (
  isodate: string | number | Date,
  locale = 'de-DE'
): string => {
  let date: Date | string = '';
  if (isodate) {
    date = new Date(isodate);

    date = date.toLocaleDateString(locale, {
      timeZone: 'UTC',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    // date = date.toISOString();
  }
  return date;
};

/**
 * Konvertiert ein Iso-Datum in das deutsche Zeit-Format.
 * @param date 2021-10-15T00:00:00Z
 * @returns parsedTime "12:30"-Format (locale abhängig)
 */
export const utcIsoDateToLocaleTime = (
  date: Date | undefined
): string | undefined => {
  let parsedTime = undefined;
  const comp = date?.toString();
  if (comp && comp.includes('T') && comp.includes(':') && comp.length === 20) {
    const time = comp.split('T')[1];
    const splitTime = time.split(':');
    parsedTime = splitTime[0] + ':' + splitTime[1];
    return parsedTime;
  }
  return date?.toString();
};

/**
 * @param isodate 2021-10-15T00:00:00Z
 * @returns date "2021-10-15"-Format (nicht locale abhängig)
 */
export const isoDateToDate = (
  isodate: string | number | Date | undefined
): string => {
  let date: Date | string = '';
  if (isodate) {
    date = new Date(isodate);
    date = date.toISOString().split('T')[0];
  }
  return date;
};

/**
 * @param isodate 2021-10-15T00:00:00Z
 * @returns date "15.10.2021 12:34"-Format (nicht locale abhängig)
 */
export const isoDateToDateAndTime = (
  isodate: string | number | Date
): string => {
  let date: Date | string = '';
  let tmp = '';
  let day = '';
  let month = '';
  let hour = '';
  let min = '';
  if (isodate) {
    date = new Date(isodate);
    day = '' + date.getDate();
    month = '' + (date.getMonth() + 1);
    hour = '' + date.getUTCHours();
    min = '' + date.getUTCMinutes();
    if (day.length == 1) day = '0' + day;
    if (month.length == 1) month = '0' + month;
    if (hour.length == 1) hour = '0' + hour;
    if (min.length == 1) min = '0' + min;

    tmp =
      day + '.' + month + '.' + date.getFullYear() + ', ' + hour + ':' + min;
    date = tmp;
  }
  return date;
};

/**
 * @param date "2021-10-15"-Format
 * @returns isoFormat 2021-10-15T00:00:00Z
 */
export const dateToIsoDate = (date: string | number | Date): string => {
  let isoFormat: Date | string = '';
  if (date) {
    isoFormat = new Date(date);
    isoFormat = isoFormat.toISOString();
  }
  return isoFormat;
};

/**
 * @param date 2021-10-15T00:00:00Z
 * @param time 10:00
 * @returns date + 2021-10-15T10:00:00.000Z
 */
export const appendNewTimeToDate = (
  date: string | Date,
  time: string
): string | null => {
  if (date) {
    time = time || '00:00';
    date = new Date(date);
    date.setUTCHours(Number(time.split(':')[0]));
    date.setUTCMinutes(Number(time.split(':')[1]));
    return date.toISOString();
  } else {
    return null;
  }
};

/**
 * Calculate the actually Date with Offset-Days to next WeekDay
 * @param offset number  of Days
 * @param maxTime boolean
 * @returns date actually Date + Offset  ,if maxTime then max Hours,mins and seconds
 */
export const calcCurrentDate = (offset: number, maxTime: boolean): string => {
  const date = new Date();

  if (maxTime) {
    date.setUTCHours(23, 59, 59);
  } else {
    date.setUTCHours(0, 0, 0);
  }
  date.setDate(date.getDate() + offset);
  while (date.getDay() === 6 || date.getDay() === 0) {
    date.setDate(date.getDate() + 1);
  }
  return isoDateToDate(date);
};

export const nextWorkingDayFromDate = (
  from_Date: string,
  zustelldatumOffset: string
): string => {
  if (from_Date.trim() === '') return nextWorkingDay();

  const fromDate = new Date(from_Date);

  const next = new Date(fromDate);
  if (!zustelldatumOffset) zustelldatumOffset = '1';
  next.setDate(next.getDate() + parseInt(zustelldatumOffset, 10));

  while (next.getDay() === 6 || next.getDay() === 0) {
    next.setDate(next.getDate() + 1);
  }
  const ret = isoDateToDate(next.toISOString());

  return isoDateToDate(next.toISOString());
};
/**
 * Calculates the next working Day
 *
 * @returns the next working Day as an ISO String
 */
export const nextWorkingDay = (): string => {
  const today = new Date();
  const next = new Date(today);
  next.setDate(next.getDate() + 1);

  while (next.getDay() === 6 || next.getDay() === 0) {
    next.setDate(next.getDate() + 1);
  }

  return isoDateToDate(next.toISOString());
};

export const byDatumAscending = (
  a: SendungsstatusModel,
  b: SendungsstatusModel
) => {
  if (a?.date && b?.date) {
    return a.date < b.date ? -1 : 1;
  }

  return a.date ? -1 : 1;
};
